import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Trash2, Edit, Plus, Building2, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { RichTextEditor } from "@/components/admin/RichTextEditor";

interface Event {
  id: number;
  title: string;
  event_date?: string;
}

interface Sponsor {
  id: number;
  name: string;
  image_url: string | null;
  description: string | null;
  sort_order: number;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

function resolveImageUrl(imageUrl: string | null): string | null {
  if (!imageUrl) return null;
  if (imageUrl.startsWith("http")) return imageUrl;
  return `${API_BASE_URL}${imageUrl}`;
}

export function SponsorsManager() {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState<Sponsor | null>(null);
  const [newSponsor, setNewSponsor] = useState({ name: "", image_url: "", description: "" });
  const [uploading, setUploading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadSponsors(selectedEventId);
    }
  }, [selectedEventId]);

  const loadEvents = async () => {
    try {
      const response = await api.get<Event[]>("/api/events");
      if (response.success && response.data) {
        setEvents(response.data);
        if (response.data.length > 0) {
          setSelectedEventId(response.data[0].id);
        }
      }
    } catch {
      alert("Failed to load events");
    }
  };

  const loadSponsors = async (eventId: number) => {
    try {
      const response = await api.get<Sponsor[]>(`/api/sponsors/event/${eventId}`);
      if (response.success && response.data) {
        setSponsors(response.data);
      }
    } catch {
      alert("Failed to load sponsors");
    }
  };

  const handleAdd = async () => {
    if (!newSponsor.name) {
      alert("Please fill in sponsor name");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post<Sponsor[]>("/api/sponsors", {
        ...newSponsor,
        event_id: selectedEventId ?? undefined,
      });
      if (response.success && response.data) {
        setSponsors(response.data);
        setNewSponsor({ name: "", image_url: "", description: "" });
        setIsDialogOpen(false);
        alert("Sponsor added successfully");
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to add sponsor";
      alert(`Failed to add sponsor: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingSponsor) return;

    setLoading(true);
    try {
      const response = await api.put<Sponsor>(`/api/sponsors/${editingSponsor.id}`, editingSponsor);
      if (response.success && response.data) {
        setSponsors(sponsors.map(s => s.id === editingSponsor.id ? response.data! : s));
        setEditingSponsor(null);
        setIsDialogOpen(false);
        alert("Sponsor updated successfully");
      }
    } catch (error) {
      alert("Failed to update sponsor");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this sponsor?")) return;

    setLoading(true);
    try {
      const response = await api.delete(`/api/sponsors/${id}`);
      if (response.success) {
        setSponsors(sponsors.filter(s => s.id !== id));
        alert("Sponsor deleted successfully");
      }
    } catch (error) {
      alert("Failed to delete sponsor");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await api.upload(file);

      if (result.success && result.data?.imageUrl) {
        if (editingSponsor) {
          setEditingSponsor({ ...editingSponsor, image_url: result.data.imageUrl });
        } else {
          setNewSponsor({ ...newSponsor, image_url: result.data.imageUrl });
        }
      } else {
        alert('Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-serif text-3xl text-primary mb-2">Sponsors</h1>
          <p className="text-muted-foreground">Manage event sponsors</p>
        </div>
      </div>

      {/* Event selector */}
      <Card className="p-6">
        <div>
          <Label htmlFor="sponsors-event">Select Event</Label>
          <Select
            value={selectedEventId?.toString() || ""}
            onValueChange={(value) => setSelectedEventId(parseInt(value))}
          >
            <SelectTrigger id="sponsors-event" className="mt-1">
              <SelectValue placeholder="Select an event" />
            </SelectTrigger>
            <SelectContent>
              {events.map((event) => (
                <SelectItem key={event.id} value={event.id.toString()}>
                  {event.title}
                  {event.event_date && ` (${event.event_date})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {selectedEventId && (
      <>
      <div className="flex justify-end">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                setEditingSponsor(null);
                setNewSponsor({ name: "", image_url: "", description: "" });
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Sponsor
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingSponsor ? "Edit Sponsor" : "Add New Sponsor"}</DialogTitle>
              <DialogDescription>
                {editingSponsor ? "Update the sponsor details below." : "Add a new sponsor to the event."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Sponsor Name</Label>
                <Input
                  id="name"
                  value={editingSponsor ? editingSponsor.name : newSponsor.name}
                  onChange={(e) =>
                    editingSponsor
                      ? setEditingSponsor({ ...editingSponsor, name: e.target.value })
                      : setNewSponsor({ ...newSponsor, name: e.target.value })
                  }
                  placeholder="Enter sponsor name"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <RichTextEditor
                  content={editingSponsor ? editingSponsor.description || "" : newSponsor.description}
                  onChange={(val) =>
                    editingSponsor
                      ? setEditingSponsor({ ...editingSponsor, description: val })
                      : setNewSponsor({ ...newSponsor, description: val })
                  }
                  placeholder="Enter sponsor description"
                  minHeight="100px"
                />
              </div>
              <div>
                <Label>Sponsor Logo</Label>
                <div className="flex gap-2 mt-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBrowseClick}
                    disabled={uploading}
                    className="flex-1"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? "Uploading..." : "Browse Files"}
                  </Button>
                  {(editingSponsor?.image_url || newSponsor.image_url) && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (editingSponsor) {
                          setEditingSponsor({ ...editingSponsor, image_url: "" });
                        } else {
                          setNewSponsor({ ...newSponsor, image_url: "" });
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {(editingSponsor?.image_url || newSponsor.image_url) && (
                  <div className="mt-4">
                    <Label>Preview</Label>
                    <div className="mt-2 rounded-lg overflow-hidden border">
                      <img
                        src={resolveImageUrl(editingSponsor?.image_url ?? newSponsor.image_url) ?? ""}
                        alt="Sponsor logo preview"
                        className="w-full h-32 object-contain bg-background"
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={editingSponsor ? handleUpdate : handleAdd} disabled={loading}>
                  {loading ? "Saving..." : editingSponsor ? "Update" : "Add"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Logo</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="hidden md:table-cell">Description</TableHead>
              <TableHead className="w-20 text-center">Order</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sponsors.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  No sponsors for this event yet.
                </TableCell>
              </TableRow>
            )}
            {sponsors.map((sponsor) => {
              const logoUrl = resolveImageUrl(sponsor.image_url);
              const descriptionText = sponsor.description
                ? sponsor.description.replace(/<[^>]+>/g, "").trim()
                : null;
              return (
                <TableRow key={sponsor.id}>
                  <TableCell>
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={`${sponsor.name} logo`}
                        className="w-10 h-10 object-contain rounded border bg-muted"
                      />
                    ) : (
                      <div className="w-10 h-10 flex items-center justify-center rounded border bg-muted">
                        <Building2 className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{sponsor.name}</TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground text-sm max-w-xs truncate">
                    {descriptionText || <span className="italic">No description</span>}
                  </TableCell>
                  <TableCell className="text-center text-sm text-muted-foreground">
                    {sponsor.sort_order}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingSponsor(sponsor);
                          setIsDialogOpen(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(sponsor.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
      </>
      )}
    </div>
  );
}
